/* eslint-disable camelcase */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as crypto from 'node:crypto';
import * as _ from 'lodash';
import * as utils from '../utils';
import * as config from './config';
import { getLogger } from './logger';
import { unzipFile } from '../utils/fsUtils';
import { TESTIM_BROWSER_DIR } from '../utils';
import { featureFlagsService } from './featureFlagsService';
import { CLI_MODE, mobileWeb, gridTypes } from './constants';
import { LambdatestService } from '../services/lambdatestService';
import { checkForbiddenKeys } from '../services/customCapabilitiesService';
import { cloneDeep } from 'lodash';
import type { Agent } from 'agent-base';
import type { Capabilities } from '@wdio/types';
import type { RunnerMode } from '../runners/runnerUtils';
import type { ProjectData, RunnerOptions, SaucelabsOptions } from '../runOptions';
import type { SerializedGrid } from '../services/gridService';
import type { ICapabilities } from 'services/src/commons/mongo/models/customCapabilities';

const logger = getLogger('testim-desired-capabilities-builder');

type RemoteOptions = Capabilities.WebdriverIOConfig;

const LOG_LEVEL = config.WEBDRIVER_DEBUG ? 'debug' : 'silent';
const CONTENT_SETTING = {
    CONTENT_SETTING_DEFAULT: 0,
    CONTENT_SETTING_ALLOW: 1,
    CONTENT_SETTING_BLOCK: 2,
    CONTENT_SETTING_ASK: 3,
};
const DEFAULT_CHROME_OPTIONS_ARGS = [
    '--disable-popup-blocking',
    '--ignore-gpu-blacklist',
    '--auto-select-desktop-capture-source=Entire screen',
    '--ignore-certificate-errors',
    // Disable built-in Google Translate service
    '--disable-features=TranslateUI',
    // Disable various background network services, including extension updating,
    // safe browsing service, upgrade detector, translate, UMA
    '--disable-background-networking',
    // Disable syncing to a Google account
    '--disable-sync',
    // Disable reporting to UMA, but allows for collection
    '--metrics-recording-only',
    // Disable installation of default apps on first run
    '--disable-default-apps',
    // Mute any audio
    '--mute-audio',
    // Skip first run wizards
    '--no-first-run',
    '--disable-back-forward-cache',
];
const EXTENSIONS_DIR = path.join(TESTIM_BROWSER_DIR, 'extensions');
const TESTIM_HEADLESS_UNZIPPED_EXTENSION_PATH = path.join(EXTENSIONS_DIR, 'testim-headless');
// If the custom extension is a zip file, we unzip and rename it to custom-extension
const CUSTOM_UNZIPPED_EXTENSION_PATH = path.join(EXTENSIONS_DIR, 'custom-extension');


interface SeleniumOpts extends Pick<RemoteOptions, 'user' | 'key'> {
    /** @deprecated please use hostname instead */
    host: string;
    hostname: string;
    port: number;
    path: string;
    protocol: string;
    logLevel: 'silent' | 'debug';
    connectionRetryTimeout: number;
    connectionRetryCount: number;
    getSessionTimeout: number;
    getSessionRetries: number;
    deprecationWarnings?: boolean;
    desiredCapabilities: {
        javascriptEnabled?: boolean;
        locationContextEnabled?: boolean;
        handlesAlerts?: boolean;
        rotatable?: boolean;
        acceptSslCerts?: boolean;
        unexpectedAlertBehaviour?: string;
        nativeEvents?: boolean;
        testName?: string;
        version?: string;
        browserName?: string;
        'moz:firefoxOptions'?: { prefs: any; args: string[] };
        testim_firefox_profile?: string;
        firefox_profile?: string;
        'aws:maxDurationSecs'?: number;
        'aws:idleTimeoutSecs'?: number;
        'ms:edgeChromium'?: boolean;
        browserVersion?: string;
        platform?: string;
        platformName?: string;
        acceptInsecureCerts?: boolean;
        unhandledPromptBehavior?: string;
        'ms:edgeOptions'?: SeleniumOpts['desiredCapabilities']['chromeOptions'];
        edgeOptions?: SeleniumOpts['desiredCapabilities']['chromeOptions'];
        'goog:chromeOptions'?: SeleniumOpts['desiredCapabilities']['chromeOptions'];
        chromeOptions?: {
            prefs: any;
            w3c?: boolean;
            args?: string[];
            extensions?: string[];
            localState?: any;
            mobileEmulation?: { deviceMetrics?: { width: number; height: number; pixelRatio: number }; userAgent: string };
        };
        loadExtension?: string[]; // exists for Lambdatest
        [key: string]: any;
    };
    capabilities?: { alwaysMatch: SeleniumOpts['desiredCapabilities']; firstMatch: [{}] };
    headers?: {};
    agent?: Agent;
}

const getHash = (...str: string[]) => crypto.createHash('sha256').update(str.join('')).digest('hex');

const isDFGrid = (gridInfo: any) => gridInfo.type === gridTypes.DEVICE_FARM || (gridInfo.type === gridTypes.HYBRID && gridInfo.provider === 'devicefarm');

/**  @see https://webdriver.io/docs/capabilities/#browser-specific-capability-extensions */
const convertToNewCapabilitiesFormat = (opts: SeleniumOpts) => {
    const { desiredCapabilities } = opts;
    if (Object.hasOwn(desiredCapabilities, 'version')) {
        desiredCapabilities.browserVersion = desiredCapabilities.version;
        delete desiredCapabilities.version;
    }
    if (Object.hasOwn(desiredCapabilities, 'platform')) {
        desiredCapabilities.platformName = desiredCapabilities.platform;
        delete desiredCapabilities.platform;
    }
    if (Object.hasOwn(desiredCapabilities, 'acceptSslCerts')) {
        desiredCapabilities.acceptInsecureCerts = desiredCapabilities.acceptSslCerts;
        delete desiredCapabilities.acceptSslCerts;
    }
    if (Object.hasOwn(desiredCapabilities, 'unexpectedAlertBehaviour')) {
        desiredCapabilities.unhandledPromptBehavior = desiredCapabilities.unexpectedAlertBehaviour;
        delete desiredCapabilities.unexpectedAlertBehaviour;
    }
    if (Object.hasOwn(desiredCapabilities, 'chromeOptions')) {
        desiredCapabilities['goog:chromeOptions'] ??= desiredCapabilities.chromeOptions;
        delete desiredCapabilities.chromeOptions;
    }
    if (Object.hasOwn(desiredCapabilities, 'edgeOptions')) {
        desiredCapabilities['ms:edgeOptions'] ??= desiredCapabilities.edgeOptions;
        delete desiredCapabilities.edgeOptions;
    }
    if (Object.hasOwn(desiredCapabilities, 'firefoxOptions')) {
        desiredCapabilities['moz:firefoxOptions'] ??= desiredCapabilities.firefoxOptions;
        delete desiredCapabilities.firefoxOptions;
    }

    if (desiredCapabilities['goog:chromeOptions']?.w3c === false) {
        desiredCapabilities['goog:chromeOptions'].w3c = true;
    }

    if ('username' in opts.desiredCapabilities) {
        opts.user = opts.desiredCapabilities.username as string;
        delete opts.desiredCapabilities.username;
    }
    if ('accessKey' in opts.desiredCapabilities) {
        opts.key = opts.desiredCapabilities.accessKey as string;
        delete opts.desiredCapabilities.accessKey;
    }

    let unsupportedCapsFields;
    try {
        unsupportedCapsFields = featureFlagsService.flags.unsupportedCapsFields.getValue();
        const unsupportedFields = JSON.parse(unsupportedCapsFields);
        if (!Array.isArray(unsupportedFields)) {
            return;
        }

        (unsupportedFields as (keyof SeleniumOpts['desiredCapabilities'])[]).forEach((field) => {
            delete desiredCapabilities[field];
        });
    } catch {
        logger.error('Fail to parse "unsupportedCapsFields" flag', { unsupportedCapsFields });
    }
};

function buildSafariOptions(opts: SeleniumOpts, browserName: string) {
    const safariOptions = {
        browserName: 'safari',
        ...(browserName === 'safari technology preview' && { 'safari.options': { technologyPreview: true } }),
    };
    Object.assign(opts.desiredCapabilities, safariOptions);
    return opts;
}

function readFileToBase64(fileLocation: string) {
    return fs.readFileSync(fileLocation, { encoding: 'base64' });
}

function setCustomExtension(customExtensionLocalLocation: string | undefined, extensions: string[], lambdatestService?: LambdatestService, useLocalChromeDriver?: boolean) {
    if (lambdatestService?.isLambdatestRun() && utils.isURL(customExtensionLocalLocation!)) {
        return null;
    }

    if (customExtensionLocalLocation) {
        if (useLocalChromeDriver) {
            if (customExtensionLocalLocation.endsWith('.zip')) {
                unzipFile(customExtensionLocalLocation, CUSTOM_UNZIPPED_EXTENSION_PATH);
                return CUSTOM_UNZIPPED_EXTENSION_PATH;
            }
            return customExtensionLocalLocation;
        }
        const extStr = readFileToBase64(customExtensionLocalLocation);
        // for debugging purpose should be removed in the future
        logger.info(`adding extension: custom, path: ${customExtensionLocalLocation} length: ${extStr.length} hash: ${getHash(extStr)} current extension count: ${extensions.length}`);
        extensions.push(extStr);
    }
    return null;
}

/**
 * This is where we configure the extension for the Testim run.
 * There are 2 options to determine which extension to use:
 *
 * 1. Use the --load-extension argument: The extension must be unpacked on the machine that is running the test, and we provide the path.
 *    For example, --load-extension=/opt/testim-headless
 *
 * 2. Pass the extension as a base64-encoded string.
 */
function setTestimExtension(browserOptions: RunnerOptions, extensions: string[], args: string[], predefinedTestimExtension: string | null, lambdatestService?: LambdatestService, useLocalChromeDriver?: boolean) {
    // If it's a lambdatest run, we only send the link to the extension capabilities.alwaysMatch.loadExtension
    if (lambdatestService?.isLambdatestRun()) {
        return null;
    }

    const testimHeadlessExtensionDownloadUrl = browserOptions.testimHeadlessExtensionDownloadUrl;

    // if it's a testim grid, we give it the path to the extension since it's already unpacked on the grid
    if ((browserOptions.ext || predefinedTestimExtension) && !testimHeadlessExtensionDownloadUrl) {
        const extFromOptions = typeof browserOptions.ext === 'string' ? browserOptions.ext : `${__dirname}/..`;
        const ext = predefinedTestimExtension || extFromOptions;
        const loadExt = `--load-extension=${ext}`;
        logger.info(`adding extension: testim unpacked , path: ${ext}`);
        args.push(loadExt);
        return null;
    }

    const zipFileSuffix = browserOptions.canary ? '-master.zip' : '.zip';
    let fileName = `testim-headless${zipFileSuffix}`;
    if (!browserOptions.extensionPath && testimHeadlessExtensionDownloadUrl) {
        fileName = path.basename(testimHeadlessExtensionDownloadUrl);
    }

    const filePath = path.join(process.cwd(), fileName);
    if (useLocalChromeDriver) {
        // Testim extension is being downloaded as a zip file, we need to unzip it to the extensions directory
        unzipFile(filePath, TESTIM_HEADLESS_UNZIPPED_EXTENSION_PATH);
        return TESTIM_HEADLESS_UNZIPPED_EXTENSION_PATH;
    }

    const extStr = readFileToBase64(filePath);
    // for debugging purpose should be removed in the future
    logger.info(`adding extension: testim zipped, path: ${filePath} length: ${extStr.length} hash: ${getHash(extStr)} current extension count: ${extensions.length}`);
    extensions.push(extStr);
    return null;
}

function _buildChromiumOptions(
    opts: SeleniumOpts,
    browserOptions: RunnerOptions,
    testRunConfig: any,
    customExtensionLocalLocation: string | undefined,
    gridInfo: any,
    predefinedTestimExtension: string | null,
    lambdatestService?: LambdatestService,
    mode?: RunnerMode,
) {
    // * Make sure to add any referenced attributes of the function arguments to the hash created in buildChromiumOptions * //

    const browserName = testRunConfig.seleniumName || testRunConfig.browserValue;
    const extensions: string[] = [];
    const args = [...DEFAULT_CHROME_OPTIONS_ARGS];
    if (browserOptions.headless) {
        args.push('--headless');
    }
    const chromiumOptions: NonNullable<SeleniumOpts['desiredCapabilities']['chromeOptions']> = {
        prefs: {
            'profile.default_content_setting_values.popups': CONTENT_SETTING.CONTENT_SETTING_ALLOW,
            // allow multiple download files
            'profile.default_content_setting_values.automatic_downloads': CONTENT_SETTING.CONTENT_SETTING_ALLOW,
            // disable pdf viewer
            'plugins.always_open_pdf_externally': true,
            // unintuitively stops download protection ("Dangerous file blocked")
            // if needed in the future, consider adding --safebrowsing-disable-download-protection or --safebrowsing-disable-extension-blacklist to the chrome flags
            'safebrowsing.enabled': true,
            'profile.content_settings.exceptions.clipboard': {
                '[*.],*': { last_modified: Date.now(), setting: 1 },
            },
            'download.allow_office_viewer_for_download': false,
            'profile.password_manager_leak_detection': false,
        },
        w3c: true,
    };

    if (isDFGrid(gridInfo)) {
        const insecureOriginsForDF = featureFlagsService.flags.DFAcceptInsecureOrigins.getValue();
        if (typeof insecureOriginsForDF === 'string') {
            // To prevent DF failures on insecure origins (e.g our JSbin that works on HTTP): https://peter.sh/experiments/chromium-command-line-switches/#unsafely-treat-insecure-origin-as-secure
            args.push(`--unsafely-treat-insecure-origin-as-secure=${insecureOriginsForDF}`);
        }
        chromiumOptions.prefs['download.default_directory'] = 'C:\\Users\\testnode';
        opts.desiredCapabilities.version = featureFlagsService.flags.DFBrowserVersion.getValue();
        opts.desiredCapabilities['aws:maxDurationSecs'] = 2400; // Maximum duration of the session before it is forcibly closed, in seconds. Range: 180 to 2400.
        opts.desiredCapabilities['aws:idleTimeoutSecs'] = 60; // Maximum delay between WebDriver commands before the session is forcibly closed. Range: 30 to 900.
    }

    if (isDFGrid(gridInfo) && browserName === 'MicrosoftEdge') {
        opts.desiredCapabilities['ms:edgeChromium'] = true;
    }

    if (browserOptions.chromeExtraPrefs) {
        Object.assign(chromiumOptions.prefs, browserOptions.chromeExtraPrefs);
    }

    if (browserOptions.chromeExtraArgs) {
        browserOptions.chromeExtraArgs.forEach((arg) => args.push(`--${arg}`));
    }

    try {
        const disableChromeFeatures = getDisabledChromeFeatures();
        if (disableChromeFeatures.length) {
            disableChromeFeatures.forEach((feature: string) => args.push(`--disable-features=${feature}`));
        }
    } catch {
        // if we couldn't parse the flag, we don't want to block the run, silently fail into oblivion
    }

    if (browserOptions.chromeBlockLocation) {
        chromiumOptions.prefs['profile.default_content_setting_values.geolocation'] = CONTENT_SETTING.CONTENT_SETTING_BLOCK;
    }

    if (browserOptions.chromeUserDataDir) {
        args.push(`--user-data-dir=${browserOptions.chromeUserDataDir}`);
    }

    if (browserOptions.projectData?.defaults?.disableChromiumGpu) {
        args.push('--disable-gpu');
    }

    Object.assign(opts.desiredCapabilities, { browserName });

    function setMobileEmulationSettings() {
        if (testRunConfig.mobileEmulation) {
            chromiumOptions.mobileEmulation = {
                deviceMetrics: {
                    width: testRunConfig.mobileEmulation.device.width,
                    height: testRunConfig.mobileEmulation.device.height + mobileWeb.MOBILE_WEB_REMOTE_RUN_HEADER_SPACING,
                    pixelRatio: testRunConfig.mobileEmulation.device.deviceScaleFactor,
                },
                userAgent: testRunConfig.mobileEmulation.userAgent,
            };
        }
    }

    setMobileEmulationSettings();

    setCustomExtension(customExtensionLocalLocation, extensions, lambdatestService);
    if (mode === CLI_MODE.EXTENSION) {
        setTestimExtension(browserOptions, extensions, args, predefinedTestimExtension, lambdatestService);
    }
    if (extensions.length > 0) {
        chromiumOptions.extensions = extensions;
    }

    if (browserOptions.disableCookiesSameSiteNoneRequiresSecure) {
        chromiumOptions.localState = {
            'browser.enabled_labs_experiments': ['cookies-without-same-site-must-be-secure@2'],
        };
    }

    chromiumOptions.args = args;
    const optionsKey = { MicrosoftEdge: 'edgeOptions', chrome: 'chromeOptions' }[browserName as 'MicrosoftEdge' | 'chrome'] as 'edgeOptions' | 'chromeOptions';
    const vendor = { MicrosoftEdge: 'ms', chrome: 'goog' }[browserName as 'MicrosoftEdge' | 'chrome'] as 'ms' | 'goog';

    if (LambdatestService.isLambdatestGrid(gridInfo)) {
        delete chromiumOptions.w3c;
    }

    if (browserOptions.oldCapabilities && gridInfo.type !== 'testimEnterprise' && !lambdatestService?.isLambdatestRun()) {
        opts.desiredCapabilities[optionsKey] = chromiumOptions;
    }

    if (browserOptions.w3cCapabilities || gridInfo.type === 'testimEnterprise') {
        opts.desiredCapabilities[`${vendor}:${optionsKey}` as 'ms:edgeOptions' | 'goog:chromeOptions'] = chromiumOptions;
    }

    return opts;
}

const buildChromiumOptions = _.memoize(_buildChromiumOptions, (opts, browserOptions, testRunConfig, customExtensionLocalLocation, gridInfo, predefinedTestimExtension) => {
    // Only hash the attributes which are used in _buildChromiumOptions, some others (which are irrelevant) change and invalidate the hash
    const stringOptsDesiredCapabilities = JSON.stringify(opts.desiredCapabilities);
    const stringBrowserOptions = JSON.stringify(_.omit(browserOptions, 'runParams'));
    const stringTestRunConfig = JSON.stringify(testRunConfig);
    const stringGridInfoType = JSON.stringify(gridInfo.type);
    return getHash(stringOptsDesiredCapabilities, stringBrowserOptions, stringTestRunConfig, customExtensionLocalLocation!, stringGridInfoType, predefinedTestimExtension!);
});

// list of content type that Firefox browser will not open the download popup and start the download automaticlly into Downloads folder
const FIREFOX_FILE_NEVER_ASK = [
    'application/force-download',
    'application/pdf',
    'application/x-pdf',
    'application/acrobat',
    'applications/vnd.pdf',
    'text/pdf',
    'text/x-pdf',
    'application/vnd.cups-pdf',
];

function buildFirefoxOptions(opts: SeleniumOpts, browserOptions: RunnerOptions, lambdatestService?: LambdatestService) {
    const fireFoxPrefs = {
        'pdfjs.disabled': true, //disable pdf viewer. Otherwise, the pdf viewer takes over when we download a pdf.
    };

    if (featureFlagsService.flags.autoSaveDownloadFileFireFox.isEnabled()) {
        Object.assign(fireFoxPrefs, {
            /* eslint-disable max-len */
            'browser.helperApps.neverAsk.saveToDisk': FIREFOX_FILE_NEVER_ASK.join(','),
            'browser.helperApps.neverAsk.openFile': FIREFOX_FILE_NEVER_ASK.join(','),
            'browser.helperApps.alwaysAsk.force': false,
            'browser.download.manager.useWindow': false, // A boolean value indicating whether or not to use the Downloads window (true) or display download status in the status bar (false) in the browser window.
            'browser.download.manager.focusWhenStarting': false, // A boolean value that indicates whether or not to focus the Download Manager window when a download begins.
            'browser.download.manager.alertOnEXEOpen': false, // A boolean value that indicates whether the UI Should alert the user about the dangers of opening (executing) an EXE. The user may check a "don't ask" box on the UI to toggle this.
            'browser.download.manager.showWhenStarting': false, // A boolean value that indicates whether or not to show the Downloads window when a download begins.
            'browser.download.manager.closeWhenDone': true, // As boolean value indicating whether or not the Downloads window should close automatically when downloads are completed.
            'browser.download.manager.showAlertOnComplete': false, // A boolean value that indicates whether or not an alert should be shown when downloads complete.
            /* eslint-enable max-len */
        });
    }

    Object.assign(
        opts.desiredCapabilities,
        {
            acceptInsecureCerts: true,
            browserName: 'firefox',
            marionette: true,
            'moz:firefoxOptions': {
                prefs: fireFoxPrefs,
            },
        },
        lambdatestService?.isLambdatestRun() ? { 'LT:Options': { enableCustomTranslation: true } } : {},
    );

    if (browserOptions.disableCookiesSameSiteNoneRequiresSecure) {
        opts.desiredCapabilities['moz:firefoxOptions']!.prefs['network.cookie.sameSite.noneRequiresSecure'] = false;
    }

    // more interesting options
    // https://developer.mozilla.org/en-US/docs/Mozilla/Firefox/Headless_mode#Debugging_headless_Firefox
    if (browserOptions.headless) {
        if (!opts.desiredCapabilities['moz:firefoxOptions']!.args) {
            opts.desiredCapabilities['moz:firefoxOptions']!.args = [];
        }
        opts.desiredCapabilities['moz:firefoxOptions']!.args.push('-headless');
    }

    return opts;
}

function formatResolution(resolution: { width: number; height: number }) {
    return `${resolution.width}x${resolution.height}`;
}

const DEFAULT_RESOLUTION = { width: 1920, height: 1080 };
const LINUX_RESOLUTION = { width: 1920, height: 1200 };
const WINDOWS_RESOLUTION = { width: 2560, height: 1600 };
const MACOS_RESOLUTION = { width: 2360, height: 1770 }; //this is for MacOS - Sonoma,Ventura, Monterey, Big Sur, Catalina, Mojave, High Sierra, Sierra
const OSX_ELCAPITAN_OR_YOSEMITE_RESOLUTION = { width: 2048, height: 1536 };
const SUPPORTED_WINDOWS_VERSIONS = new Set(['7', '8', '8.1', '10', '11']); // windows XP is supported too but with the default resolution

function getResolutionForSL(testRunConfig: any) {
    const { platform } = testRunConfig;
    if (!platform) return formatResolution(DEFAULT_RESOLUTION);

    const windowsVersion = platform.split(' ')[1]; // in case this is actually windows OS
    if (platform.startsWith('Windows') && SUPPORTED_WINDOWS_VERSIONS.has(windowsVersion)) return formatResolution(WINDOWS_RESOLUTION);
    if (platform === 'Linux') return formatResolution(LINUX_RESOLUTION);
    if (platform.startsWith('macOS')) return formatResolution(MACOS_RESOLUTION);
    if (platform === 'OS X El Capitan' || platform === 'OS X Yosemite') return formatResolution(OSX_ELCAPITAN_OR_YOSEMITE_RESOLUTION);

    return formatResolution(DEFAULT_RESOLUTION);
}
function buildSaucelabs(browserOptions: RunnerOptions, testName: string, testRunConfig: any): Partial<SaucelabsOptions> {
    const { saucelabs } = browserOptions;
    if (saucelabs?.browserVersion) {
        // make sure to override browserVersion with the version from the run config incase it was provided from saucelabs options json file
        saucelabs.version = saucelabs.browserVersion;
    }
    if (saucelabs?.username && saucelabs.accessKey) {
        const sauceOptions: { 'sauce:options': { name: string; screenResolution?: string; appiumVersion?: string } } = { 'sauce:options': { name: testName } };
        sauceOptions['sauce:options'].screenResolution = getResolutionForSL(testRunConfig.sl);
        if (testRunConfig) {
            testRunConfig.sl.version = testRunConfig.browserValue === 'safari' ? testRunConfig.sl.safari_version : testRunConfig.sl.version;
            const appiumVersion = (saucelabs as any).appiumVersion || testRunConfig.sl.appiumVersion;
            if (appiumVersion) {
                sauceOptions['sauce:options'].appiumVersion = appiumVersion;
            }
            const keysToOmit = ['screenResolution', 'appiumVersion'];
            if (testRunConfig.browserValue !== 'safari') {
                keysToOmit.push('safari_version');
            }
            const slToSend = _.omit(cloneDeep(testRunConfig.sl), keysToOmit);
            //saucelabs options from the json file will override the options from the run config if both exist
            return _.merge(slToSend, sauceOptions, saucelabs);
        }
        //code should not reach here, we always have testRunConfig
        return _.merge(sauceOptions, saucelabs);
    }
    return {};
}

interface BrowserStackOpts {
    'browserstack.user': string;
    'browserstack.key': string;
    os_version: string;
    platform: string;
    name: string;
    'safari.options': { technologyPreview: boolean };
    buildName: string;
    projectName: string;
}

interface BuildBrowserstackParams {
    runnerOptions: RunnerOptions;
    testId: string;
    testName: string;
    testRunConfig: any;
    executionId: string;
    executionName: string;
    projectData: ProjectData;
}

function buildBrowserstack(params: BuildBrowserstackParams): { 'bstack:options': Partial<BrowserStackOpts> } {
    const { runnerOptions } = params;

    if (_.isEmpty(runnerOptions.browserstack)) {
        return { 'bstack:options': {} };
    }

    const { projectData, testRunConfig, testId, testName, executionId, executionName } = params;
    const bsOptions = { 'bstack:options': {
        buildName: `${executionName} - ${executionId}`,
        sessionName: `${testName} - ${testId}`,
            projectName: `${projectData.name} - ${projectData.projectId}`,
            ...runnerOptions.browserstack,
        },
    };

    if (!testRunConfig) {
        return bsOptions;
    }

    if (testRunConfig.browserValue === 'safari' && testRunConfig.bs.safari_version) {
        bsOptions['bstack:options'].os_version = testRunConfig.bs.safari_version;
        if (testRunConfig.bs.safari_version === '10') {
            Object.assign(bsOptions['bstack:options'], { 'safari.options': { technologyPreview: true } });
        }
    }

    return bsOptions;
}

function buildPerfecto(browserOptions: RunnerOptions) {
    if (browserOptions.perfecto) {
        return browserOptions.perfecto;
    }
    return {};
}

interface ExperitestOpts {
    accessKey: string;
    browserVersion: string;
    platformName: string;
    seleniumScreenshot: boolean;
    newSessionWaitTimeout: number;
}
function buildExperitest(browserOptions: RunnerOptions, browser: string, sessionTimeoutSec: number): Partial<ExperitestOpts> {
    if (browserOptions.experitestToken) {
        const isSafari = browser === 'safari';
        return {
            accessKey: browserOptions.experitestToken,
            browserVersion: 'latest',
            platformName: isSafari ? 'MAC' : 'WIN10',
            //Workaround to Experitest bug in take screenshot in Safari browser
            seleniumScreenshot: isSafari,
            newSessionWaitTimeout: sessionTimeoutSec,
        };
    }

    return {};
}

function buildHeaders(browserOptions: RunnerOptions, gridInfo: any = {}) {
    const { gridData = {}, gridUsername, gridPassword } = browserOptions;
    const username = gridUsername || gridData.user || gridInfo.user;
    const password = gridPassword || gridData.key || gridInfo.key;

    const headers = {} as { [header: string]: string | string[] };
    if (username && password) {
        headers.Authorization = utils.buildBasicHeader(username, password);
    }

    return headers;
}

export interface SeleniumOptions {
    projectData?: ProjectData;
    overrideConfiguration?: any;
    browserOptions: RunnerOptions;
    testId?: string;
    testName: string;
    testRunConfig: any;
    gridInfo: SerializedGrid;
    customExtensionLocalLocation: string | undefined;
    executionId: string;
    executionName?: string;
    testResultId: string;
    lambdatestService?: LambdatestService;
    printFinalCaps?: boolean;
    mode?: RunnerMode;
    customCapabilities?: ICapabilities;
    useLocalChromeDriver?: boolean;
}

export function getDisabledChromeFeatures() {
    return (featureFlagsService.flags.chromeArgsDisableFeatures.getValue() || '').split(',').filter((s) => s.trim().length);
}

export function buildSeleniumOptions(options: SeleniumOptions) {
    const {
        overrideConfiguration,
        browserOptions,
        testId,
        testName,
        testRunConfig,
        gridInfo,
        customExtensionLocalLocation,
        executionId,
        executionName,
        projectData,
        testResultId,
        lambdatestService,
        mode,
        customCapabilities,
        useLocalChromeDriver,
    } = options;

    if (gridInfo.mode === 'local') {
        const extensions: string[] = [];
        const args = [...DEFAULT_CHROME_OPTIONS_ARGS];
        const binaryLocation: { binary?: string } = {};

        if (browserOptions.headless) {
            args.push('--headless');
        }

        // @ts-expect-error These options don't exist in types, possibly only work with config file?
        if (browserOptions.silentDebuggerExtensionApi) {
            args.push('--silent-debugger-extension-api');
        }

        // @ts-expect-error These options don't exist in types, possibly only work with config file?
        if (browserOptions.remoteDebuggingPort !== undefined) {
            // @ts-expect-error These options don't exist in types, possibly only work with config file?
            args.push(`--remote-debugging-port=${browserOptions.remoteDebuggingPort}`);
        }

        try {
            const disableChromeFeatures = getDisabledChromeFeatures();
            if (disableChromeFeatures.length) {
                disableChromeFeatures.forEach((feature: string) => args.push(`--disable-features=${feature}`));
            }
        } catch {
            // if we couldn't parse the flag, we don't want to block the run, silently fail into oblivion
        }

        if (browserOptions.chromeExtraArgs) {
            browserOptions.chromeExtraArgs.forEach((arg) => args.push(`--${arg}`));
        }

        if (browserOptions.chromeBinaryLocation) {
            binaryLocation.binary = browserOptions.chromeBinaryLocation;
        }

        let unzippedTestimExtensionPath, unzippedCustomExtensionPath = null;
        if (mode !== CLI_MODE.SELENIUM) {
            unzippedTestimExtensionPath = setTestimExtension(browserOptions, extensions, args, null, lambdatestService, useLocalChromeDriver);
        }
        unzippedCustomExtensionPath = setCustomExtension(customExtensionLocalLocation, extensions, lambdatestService, useLocalChromeDriver);

        // We only using the Bidi protocol for local Chrome driver runs
        // To enable the WebExtension API, we need to enable the webSocketUrl capability and add the --enable-unsafe-extension-debugging and --remote-debugging-pipe arguments
        if (useLocalChromeDriver) {
            args.push('--enable-unsafe-extension-debugging', '--remote-debugging-pipe');
        }

        return {
            logLevel: LOG_LEVEL,
            capabilities: {
                alwaysMatch: {
                    'goog:chromeOptions': {
                        args,
                        extensions,
                        ...binaryLocation,
                    },
                    browserName: 'chrome',
                    ...(useLocalChromeDriver && { webSocketUrl: true }),
                },
                firstMatch: [{}],
            },
            path: '/wd/hub',
            hostname: 'localhost',
            port: 9515, // default chromedriver port
            // We only need to send the path to the extension to the browser if we are using local Chrome driver
            ...(useLocalChromeDriver && unzippedTestimExtensionPath && { unzippedTestimExtensionPath: unzippedTestimExtensionPath }),
            ...(useLocalChromeDriver && unzippedCustomExtensionPath && { unzippedCustomExtensionPath: unzippedCustomExtensionPath }),
        };
    }

    const { driverRequestTimeout, driverRequestRetries } = browserOptions;
    const headers = buildHeaders(browserOptions, gridInfo);
    let opts: SeleniumOpts = {
        hostname: gridInfo.host!,
        host: gridInfo.host!,
        port: gridInfo.port || 4444,
        path: gridInfo.path || '/wd/hub',
        protocol: gridInfo.protocol || 'http',
        logLevel: LOG_LEVEL,
        connectionRetryTimeout: driverRequestTimeout,
        connectionRetryCount: driverRequestRetries,
        getSessionTimeout: Math.max(lambdatestService!.getSessionTimeout!, browserOptions.getSessionTimeout),
        getSessionRetries: lambdatestService!.getSessionRetries || browserOptions.getSessionRetries,
        deprecationWarnings: false,
        desiredCapabilities: {
            acceptSslCerts: true,
            /**  Deprecated: https://webdriver.io/blog/2024/08/15/webdriverio-v9-release/#automatic-dialog-handling
            Please do not restore this capability */
            // unexpectedAlertBehaviour: 'accept', // What the browser should do with an unhandled alert before throwing out the UnhandledAlertException - automatically click on accept
        },
        ...(!_.isEmpty(headers) && { headers }),
        ...(browserOptions.proxyForGrid && { agent: new global.ProxyAgent!(global.proxyUri) as any }),
    };

    if (isDFGrid(gridInfo)) {
        browserOptions.oldCapabilities = false;
        browserOptions.w3cCapabilities = true;
        opts.desiredCapabilities = {
            unexpectedAlertBehaviour: 'accept',
        };
    }

    if (browserOptions.disableNativeEvents) {
        opts.desiredCapabilities.nativeEvents = false;
    }

    if (gridInfo.user && gridInfo.key) {
        if (gridInfo.type === 'saucelabs') {
            browserOptions.saucelabs ||= {} as any;
            browserOptions.saucelabs.username ||= gridInfo.user;
            browserOptions.saucelabs.accessKey ||= gridInfo.key;
        }

        if (gridInfo.type === 'browserstack') {
            browserOptions.browserstack ||= {} as any;
            browserOptions.browserstack['browserstack.user'] ||= gridInfo.user;
            browserOptions.browserstack['browserstack.key'] ||= gridInfo.key;
        }
    }

    if (gridInfo.key && (gridInfo.type as any) === 'perfecto') {
        browserOptions.perfecto.securityToken = gridInfo.key;
    }

    if (gridInfo.type === 'local') {
        const gridUrl = new URL(
            gridInfo.host!.startsWith('http') || gridInfo.host!.startsWith('https') ? gridInfo.host! : `${gridInfo.protocol}://${gridInfo.host!}`
          );
        opts.user = gridUrl.username;
        opts.key = gridUrl.password;
        opts.hostname = gridUrl.hostname;
        opts.host = gridUrl.hostname;
    }

    const browserTimeoutSec = Number(browserOptions.browserTimeout / 1000);
    const browser = browserOptions.browser || testRunConfig?.browserValue;

    const browserstackOptions: BuildBrowserstackParams = {
        runnerOptions: browserOptions,
        testId: testId!,
        testName,
        testRunConfig,
        executionId,
        executionName: executionName!,
        projectData: projectData!,
    };

    _.merge(
        opts.desiredCapabilities,
        buildSaucelabs(browserOptions, testName, testRunConfig),
        buildBrowserstack(browserstackOptions),
        buildPerfecto(browserOptions),
        buildExperitest(browserOptions, browser, browserTimeoutSec),
        lambdatestService?.getCapabilities(browserOptions, browser, executionId, testResultId, testName, testRunConfig, overrideConfiguration, mode),
    );

    let predefinedTestimExtension = null;
    if (!browserOptions.ext && !browserOptions.extensionPath && gridInfo.host?.endsWith('.testim.io') && !browserOptions.canary && mode === CLI_MODE.EXTENSION &&
        (browser === 'chrome' || (browser === 'edge-chromium' && featureFlagsService.flags.sendExtensionOnEdgeNonLinux.isEnabled()))) {
        // This path is available only on linux - this code handles the situation for TESDEVOPS-1797
        // TODO: make this less implicit and more intentional
        predefinedTestimExtension = '/opt/testim-headless';
    }

    switch (browser) {
        case 'chrome':
        case 'edge-chromium':
            opts = buildChromiumOptions(opts, browserOptions, testRunConfig, customExtensionLocalLocation, gridInfo, predefinedTestimExtension, lambdatestService, mode);
            break;
        case 'firefox':
            opts = buildFirefoxOptions(opts, browserOptions, lambdatestService);
            break;
        case 'safari':
        case 'safari technology preview':
            opts = buildSafariOptions(opts, browser);
            break;
        default:
            break;
    }

    if (customCapabilities) {
        checkForbiddenKeys(customCapabilities, gridInfo.type);
        _.merge(opts.desiredCapabilities, customCapabilities);
    } else {
        _.merge(opts.desiredCapabilities, browserOptions.seleniumCapsFileContent);
    }

    try {
        /**
         * Targeted custom capabilities can be added to the desired capabilities object via the addCustomCapabilities FF.
         * No targeting: { selenium_version: '3.141.59' }
         * One level targeting (either grid provider, host, browser name or browser version): { "devicefarm": { selenium_version: '3.141.59' } }
         * Two level targeting: { "internet explorer": { "11": { selenium_version: '3.141.59' } } }
         */
        const hostToProvider = {
            'hub.lambdatest.com': 'lambdatest',
            [featureFlagsService.flags.publicGridURL.getValue()]: 'testim',
            'testgrid-devicefarm.us-west-2.amazonaws.com': 'devicefarm',
        };
        const byGrid = (capabilities: any) => capabilities[gridInfo.provider!] || capabilities[opts.host] || capabilities[hostToProvider[opts.host]];
        const getTargetingGroup = (capabilities: any) =>
            byGrid(capabilities) || capabilities[opts.desiredCapabilities?.browserName as any] || capabilities[opts.desiredCapabilities?.version as any] || capabilities || {};
        const featureFlagCapabilities = JSON.parse(featureFlagsService.flags.addCustomCapabilities.getValue() || '{}');
        const targetingGroupCapabilities = getTargetingGroup(getTargetingGroup(featureFlagCapabilities));

        if (Object.keys(targetingGroupCapabilities).length) {
            logger.info(`Adding custom capabilities: ${JSON.stringify(targetingGroupCapabilities)}`);
            Object.assign(opts.desiredCapabilities, targetingGroupCapabilities);
        }
    } catch (error) {
        logger.error('Failed to load custom capabilities', {
            error,
            customCapabilities: featureFlagsService.flags.addCustomCapabilities.getValue(),
        });
    }

    if (opts.desiredCapabilities && !opts.capabilities) {
        convertToNewCapabilitiesFormat(opts);
        opts.capabilities = { alwaysMatch: opts.desiredCapabilities, firstMatch: [{}] };
        delete (opts as any).desiredCapabilities;
    }

    // Disable WebDriver BiDi protocol to prevent uncaught exceptions from BiDi command timeouts
    // (e.g., browsingContext.getTree timing out). BiDi is enabled by default in WDIO 9.
    // Note: Local Chrome driver runs (which return early above) intentionally use BiDi for extension installation.
    opts.capabilities ||= { alwaysMatch: {}, firstMatch: [{}] };
    opts.capabilities.alwaysMatch ||= {};
    opts.capabilities.alwaysMatch['wdio:enforceWebDriverClassic'] = true;

    opts.hostname = opts.host;
    if (options.printFinalCaps) {
        const originalAlwaysMatch = opts.capabilities?.alwaysMatch;
        const printObj = JSON.parse(JSON.stringify(opts));
        const alwaysMatch = printObj?.capabilities?.alwaysMatch;
        // Truncate the extensions in case some are giant base64 strings (some will just be URLs)
        const truncateExtensionString = (ext: string) => ext.substring(0, 80); // 80 semi-arbitrary, it includes our headless URL
        (['goog:chromeOptions', 'ms:edgeOptions'] as const).forEach((key) => {
            if (alwaysMatch?.[key]) {
                alwaysMatch[key].extensions = originalAlwaysMatch?.[key]?.extensions?.map(truncateExtensionString);
            }
        });
        if (alwaysMatch?.loadExtension) {
            alwaysMatch.loadExtension = originalAlwaysMatch?.loadExtension?.map(truncateExtensionString);
        }
        const util = require('node:util');
        // eslint-disable-next-line no-console
        console.log(util.inspect(printObj, { showHidden: false, depth: null, colors: true })); // use util.inspect to print the object in a more readable way (mostly print nested objects)
    }
    return opts;
}
